import { forwardRef, type ReactNode, type Ref, useContext } from "react";

import { Box, Tooltip, MotionBox, type BoxProps, Row } from "@hightouch/htui";

import { ActiveMarkerIcon } from "src/components/active-marker-icon";
import { RouterLink } from "src/router";
import { cachedNavIcon } from "src/components/layout/nav/nav-icon";
import type { AmpNavLink } from "src/components/layout/nav/sections/amp-nav-links";
import { useNavWidth } from "src/contexts/nav-width-context";

import { isSubPath, useActiveLink } from "./utils";
import { withPermission } from "src/components/permission";
import { NavItemText } from "./nav-item-text";
import { CompactNavContext } from "./compact-nav-context";
import { NavHoverRow } from "./nav-hover";
import { useHasNavHoverPill } from "./nav-hover-store";
import {
  navItemBg,
  navItemTransitions,
  productNavItemBg,
  useNavRowHeight,
} from "./nav-item-styles";

type NavItemProps = {
  isSub?: boolean;
  isNested?: boolean;
  isActive?: boolean;
  /** Whether the row is this page, where its link can't tell, as for a view picked by a query parameter. */
  isCurrentPage?: boolean;
  isActiveSection?: boolean;
  isHovered?: boolean;
  isDisabled?: boolean;
  hasCollapsedTooltip?: boolean;
  href?: string;
  activePaths?: string[];
  newTab?: boolean;
  icon?: React.ElementType;
  label?: string;
  // Overrides the accessible name (defaults to `label`); use to convey state
  // (e.g. unread) to screen readers without changing the visible label.
  ariaLabel?: string;
  rightElement?: ReactNode;
  /** A control at the row's right end, kept outside its link so the two never nest. */
  trailingAction?: ReactNode;
  /** Dots the icon, so the cue survives a collapsed nav; the label is what the dot means. */
  markerLabel?: string;
} & Pick<
  BoxProps,
  "children" | "onMouseOver" | "onMouseLeave" | "onFocus" | "onClick"
>;

export const NavItem = forwardRef<HTMLButtonElement, NavItemProps>(
  (
    {
      isSub,
      isNested,
      isActive,
      isCurrentPage,
      isActiveSection,
      isHovered,
      isDisabled,
      hasCollapsedTooltip = true,
      href,
      activePaths,
      newTab,
      icon: Icon,
      label,
      ariaLabel,
      rightElement,
      trailingAction,
      markerLabel,
      children,
      onClick,
      ...props
    },
    ref,
  ) => {
    const { isNavOpen } = useNavWidth();
    const rowHeight = useNavRowHeight();
    const isLinkActive = useActiveLink({ href, isSub, activePaths });
    const isButton = onClick && !href;
    const hasRightElement =
      rightElement !== undefined &&
      rightElement !== null &&
      rightElement !== false;
    const shouldShowRightElement = isNavOpen && hasRightElement;

    // Section headers should never have active styles
    const active =
      typeof isActiveSection !== "undefined"
        ? false
        : Boolean(typeof isActive === "undefined" ? isLinkActive : isActive);

    // The AMP nav's rows read a step darker than its group labels, which stay at text.tertiary.
    const isCompactNav = useContext(CompactNavContext);
    const standardColor =
      active || isActiveSection
        ? "primary.base"
        : isCompactNav
          ? "text.secondary"
          : "text.tertiary";
    const hoverColor =
      active || isActiveSection ? "primary.base" : "text.primary";

    const itemBg = isCompactNav ? productNavItemBg : navItemBg;
    // The product nav's hover is a flat, darker fill; only its active row is raised.
    const isRaisedOnHover = !isCompactNav || active;
    // The shared pill draws the hover fill and color, so the row keeps only its active look.
    const hasHoverPill = useHasNavHoverPill() && !isDisabled;
    const unNestedHoverStyles =
      isNested || (hasHoverPill && !active)
        ? {}
        : {
            bg: active ? itemBg.active : itemBg.hover,
            ...(isRaisedOnHover && {
              borderColor: "base.border",
              boxShadow: "buttonShadowLight",
            }),
          };

    const content = (
      <MotionBox
        layout="position"
        // MotionBox types its ref as a div; `as` renders a button when interactive.
        ref={ref as Ref<HTMLDivElement>}
        aria-label={isButton ? (ariaLabel ?? label) : undefined} // Aria-label is required for interactive elements
        as={isButton ? "button" : "div"}
        className="group"
        display="flex"
        alignItems="center"
        bg={active ? itemBg.active : "transparent"}
        borderRadius="lg"
        borderWidth="hairline"
        borderColor={active ? "base.border" : "transparent"}
        boxShadow={active ? "buttonShadowLight" : "none"}
        color={isHovered ? hoverColor : standardColor}
        gap={2}
        height={rowHeight}
        justifyContent={isNavOpen ? "flex-start" : "center"}
        overflow="hidden"
        position="relative"
        px={2.5}
        py={1.5}
        pl={isNavOpen && isNested ? 9 : 2}
        width="100%"
        zIndex={1} // Ensure item overlays background hover
        {...navItemTransitions}
        _hover={{
          ...unNestedHoverStyles,
          ...(!hasHoverPill && { color: hoverColor }),
        }}
        _focusVisible={{
          outline: "none",
          border: "none",
          boxShadow: "outline",
        }}
        _focus={{
          outline: "none !important",
        }}
        // A link takes the click itself, so Enter on it runs the handler too.
        onClick={href && !isDisabled ? undefined : onClick}
        {...props}
      >
        {Icon && (
          <Box position="relative" display="flex" flexShrink={0}>
            <Icon
              color="inherit"
              flexShrink={0}
              {...navItemTransitions}
              _groupHover={hasHoverPill ? undefined : { color: hoverColor }}
            />
            {markerLabel && (
              <Box
                as={ActiveMarkerIcon}
                role="img"
                aria-label={markerLabel}
                pos="absolute"
                // Centered just past the glyph's top-right corner, clear of the icon's strokes.
                top="-3px"
                right="-3px"
                boxSize={2}
                color="primary.base"
                pointerEvents="none"
              />
            )}
          </Box>
        )}

        {label && shouldShowRightElement ? (
          <Row
            align="center"
            flex={1}
            gap={2}
            justify="space-between"
            minWidth={0}
          >
            <NavItemText>{label}</NavItemText>
            <Box flexShrink={0}>{rightElement}</Box>
          </Row>
        ) : label ? (
          <NavItemText>{label}</NavItemText>
        ) : (
          children
        )}
      </MotionBox>
    );

    const container = isDisabled ? (
      <Box cursor="not-allowed" sx={{ div: { _hover: { bg: "none" } } }}>
        {content}
      </Box>
    ) : href ? (
      <Box
        as={RouterLink}
        flex={1}
        to={href}
        preload="intent"
        aria-label={ariaLabel ?? label}
        // Only the row whose link is this page; a section row lit for a page under it isn't the page.
        aria-current={
          (isCurrentPage ?? (active && isLinkActive)) ? "page" : undefined
        }
        // Matches the row's radius, so the focus ring hugs it.
        borderRadius="lg"
        display="flex"
        _focusVisible={{ boxShadow: "outline" }}
        onClick={onClick}
        {...(newTab ? { target: "_blank", rel: "noreferrer" } : {})}
      >
        {content}
      </Box>
    ) : (
      content
    );

    const containerWithAction = trailingAction ? (
      <Box position="relative">
        {container}
        <Box
          position="absolute"
          top="50%"
          // The row's own right padding, so the action covers the space its right element holds.
          right={2.5}
          transform="translateY(-50%)"
          zIndex={1}
        >
          {trailingAction}
        </Box>
      </Box>
    ) : (
      container
    );

    const row = hasHoverPill ? (
      <NavHoverRow hoverColor={hoverColor} hasRailTick={isNested}>
        {containerWithAction}
      </NavHoverRow>
    ) : (
      containerWithAction
    );

    if (isNested) {
      return (
        <Box
          pos="relative"
          pb={2} // Give illusion of spacing between items, without sacrificing hover state
          _last={{ pb: 0, ".active-indicator": { bottom: 2 } }}
        >
          {active && <ActiveIndicator />}
          {row}
        </Box>
      );
    }

    // Display tooltip if nav is collapsed and the item is not nested
    if (!isNavOpen && !isNested && hasCollapsedTooltip) {
      return (
        <Tooltip message={label} placement="right">
          <Box>{row}</Box>
        </Tooltip>
      );
    }

    return row;
  },
);

const indicatorStyles: BoxProps = {
  width: "2px",
  borderRadius: "full",
  pos: "absolute",
  left: "17px",
  top: 2,
  bottom: 4,
};

function ActiveIndicator() {
  return (
    <Box
      {...indicatorStyles}
      aria-hidden="true"
      className="active-indicator"
      bg="primary.base"
      zIndex={20}
    />
  );
}

NavItem.displayName = "NavItem";

/** A row for one of AMP's pages. */
export const AmpNavItem = ({
  link,
  ariaLabel,
  rightElement,
}: {
  link: AmpNavLink;
  ariaLabel?: string;
  rightElement?: ReactNode;
}) => (
  <NavItem
    isSub={isSubPath(link.href)}
    href={link.href}
    icon={cachedNavIcon(link.icon)}
    label={link.label}
    markerLabel={link.markerLabel}
    activePaths={link.activePaths}
    ariaLabel={ariaLabel}
    rightElement={rightElement}
  />
);

// biome-ignore lint/style/useComponentExportOnlyModules: HOC-wrapped component; must be defined here alongside NavItem to avoid a circular-import TDZ crash from nav-section.
export const PermissionedNavItem = withPermission(NavItem);
